"use client";

import { c as _c } from "react/compiler-runtime";
import { Children, memo, useEffect, useLayoutEffect, useMemo, useRef, type ComponentPropsWithoutRef, type ElementType, type ReactNode } from "react";
import Markdown, { type Components, type ExtraProps } from "react-markdown";
import rehypeExternalLinks from "rehype-external-links";
import rehypeSanitize from "rehype-sanitize";
import remarkGfm from "remark-gfm";
import { useQueryClient } from "@tanstack/react-query";
import type { PluggableList } from "unified";
import { useEmoji } from "@/contexts/EmojiContext";
import { useSeizeSettingsOptional } from "@/contexts/SeizeSettingsContext";
import type { ApiDrop } from "@/generated/models/ApiDrop";
import type { ApiDropNftLink } from "@/generated/models/ApiDropNftLink";
import type { ApiDropMentionedUser } from "@/generated/models/ApiDropMentionedUser";
import type { ApiDropGroupMention } from "@/generated/models/ApiDropGroupMention";
import type { ApiMentionedWave } from "@/generated/models/ApiMentionedWave";
import type { ApiDropReferencedNFT } from "@/generated/models/ApiDropReferencedNFT";
import useIsMobileScreen from "@/hooks/isMobileScreen";
import { useTweetPreviewMode } from "@/components/tweets/TweetPreviewModeContext";
import { LinkPreviewProvider, useLinkPreviewContext, type LinkPreviewToggleControl } from "@/components/waves/LinkPreviewContext";
import { primeMarketplacePreviewCacheFromNftLinks } from "@/components/waves/marketplace/common";
import { DropContentPartType, createMarkdownContentRenderers } from "./dropPartMarkdown/content";
import { highlightCodeElement } from "./dropPartMarkdown/highlight";
import { createLinkRenderer, DEFAULT_MAX_EMBED_DEPTH } from "./dropPartMarkdown/linkHandlers";
const BreakComponent = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <br />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const mergeClassNames = (...classes: Array<string | undefined>): string => classes.filter(Boolean).join(" ");
const headingClassName = "tw-text-iron-200 tw-break-words word-break";
const normalizeStringArray = (values?: readonly string[]): string[] => {
  if (!values || values.length === 0) {
    return [];
  }
  return values.filter((value): value is string => typeof value === "string" && value.length > 0);
};
type MarkdownRendererProps<T extends ElementType> = ComponentPropsWithoutRef<T> & ExtraProps & {
  children?: ReactNode | undefined;
  className?: string | undefined;
};
type MarkdownCodeProps = MarkdownRendererProps<"code"> & {
  inline?: boolean | undefined;
};
type MarkdownComponentsOptions = {
  customRenderer: (content: ReactNode | undefined) => ReactNode;
  renderParagraph: Components["p"];
  renderAnchor: Components["a"];
  renderImage: Components["img"];
};
const InlineCodeRenderer = t0 => {
  const $ = _c(14);
  let children;
  let className;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      children,
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = style;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    style = $[4];
  }
  let t1;
  if ($[5] !== style) {
    t1 = {
      ...style,
      textOverflow: "unset"
    };
    $[5] = style;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== className) {
    t2 = mergeClassNames("tw-whitespace-pre-wrap tw-break-words tw-text-iron-200", className);
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[9] !== children || $[10] !== props || $[11] !== t1 || $[12] !== t2) {
    t3 = <code {...props} style={t1} className={t2}>{children}</code>;
    $[9] = children;
    $[10] = props;
    $[11] = t1;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
const CodeBlockRenderer = t0 => {
  const $ = _c(22);
  let children;
  let className;
  let props;
  let style;
  if ($[0] !== t0) {
    ({
      children,
      className,
      style,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = props;
    $[4] = style;
  } else {
    children = $[1];
    className = $[2];
    props = $[3];
    style = $[4];
  }
  const codeRef = useRef(null);
  let t1;
  if ($[5] !== children) {
    t1 = Children.toArray(children).map(_temp).join("");
    $[5] = children;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const codeText = t1;
  let t2;
  if ($[7] !== className) {
    t2 = typeof className === "string" ? /language-([\w+-]+)/.exec(className) : null;
    $[7] = className;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const match = t2;
  const language = match?.[1] ?? null;
  let t3;
  let t4;
  if ($[9] !== codeText || $[10] !== language) {
    t3 = () => {
      if (globalThis.window === undefined) {
        return;
      }
      const element = codeRef.current;
      if (!element) {
        return;
      }
      if (!codeText || codeText.trim() === "") {
        return;
      }
      highlightCodeElement(element, language).catch(_temp2);
    };
    t4 = [codeText, language];
    $[9] = codeText;
    $[10] = language;
    $[11] = t3;
    $[12] = t4;
  } else {
    t3 = $[11];
    t4 = $[12];
  }
  useEffect(t3, t4);
  let t5;
  if ($[13] !== style) {
    t5 = {
      ...style,
      textOverflow: "unset"
    };
    $[13] = style;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== className) {
    t6 = mergeClassNames("tw-whitespace-pre-wrap tw-break-words tw-text-iron-200", className);
    $[15] = className;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== children || $[18] !== props || $[19] !== t5 || $[20] !== t6) {
    t7 = <code {...props} ref={codeRef} style={t5} className={t6}>{children}</code>;
    $[17] = children;
    $[18] = props;
    $[19] = t5;
    $[20] = t6;
    $[21] = t7;
  } else {
    t7 = $[21];
  }
  return t7;
};
const CodeRenderer = t0 => {
  const $ = _c(6);
  let inline;
  let props;
  if ($[0] !== t0) {
    ({
      inline,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = inline;
    $[2] = props;
  } else {
    inline = $[1];
    props = $[2];
  }
  let t1;
  if ($[3] !== inline || $[4] !== props) {
    t1 = inline ? <InlineCodeRenderer {...props} /> : <CodeBlockRenderer {...props} />;
    $[3] = inline;
    $[4] = props;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  return t1;
};
const createMarkdownComponents = ({
  customRenderer,
  renderParagraph,
  renderAnchor,
  renderImage
}: MarkdownComponentsOptions): Components => {
  const createHeadingRenderer = <T extends ElementType,>(Tag: T) => {
    const HeadingRenderer = t0 => {
      const $ = _c(14);
      let children;
      let className;
      let props;
      if ($[0] !== t0) {
        ({
          children,
          className,
          ...props
        } = t0);
        $[0] = t0;
        $[1] = children;
        $[2] = className;
        $[3] = props;
      } else {
        children = $[1];
        className = $[2];
        props = $[3];
      }
      const t1 = props as Record<string, unknown>;
      let t2;
      if ($[4] !== className) {
        t2 = mergeClassNames(headingClassName, className);
        $[4] = className;
        $[5] = t2;
      } else {
        t2 = $[5];
      }
      let t3;
      if ($[6] !== t1 || $[7] !== t2) {
        t3 = {
          ...t1,
          className: t2
        };
        $[6] = t1;
        $[7] = t2;
        $[8] = t3;
      } else {
        t3 = $[8];
      }
      const mergedProps = t3;
      const t4 = mergedProps as any;
      let t5;
      if ($[9] !== children) {
        t5 = customRenderer(children);
        $[9] = children;
        $[10] = t5;
      } else {
        t5 = $[10];
      }
      let t6;
      if ($[11] !== t4 || $[12] !== t5) {
        t6 = <Tag {...t4}>{t5}</Tag>;
        $[11] = t4;
        $[12] = t5;
        $[13] = t6;
      } else {
        t6 = $[13];
      }
      return t6;
    };
    HeadingRenderer.displayName = `MarkdownHeading(${typeof Tag === "string" ? Tag : "component"})`;
    return HeadingRenderer;
  };
  const ListItemRenderer = t0 => {
    const $ = _c(12);
    let children;
    let className;
    let props;
    if ($[0] !== t0) {
      const {
        children: t1,
        className: t2,
        node: _node,
        ...t3
      } = t0;
      children = t1;
      className = t2;
      props = t3;
      $[0] = t0;
      $[1] = children;
      $[2] = className;
      $[3] = props;
    } else {
      children = $[1];
      className = $[2];
      props = $[3];
    }
    let t1;
    if ($[4] !== className) {
      t1 = mergeClassNames("word-break tw-break-words tw-text-md tw-text-iron-200", className);
      $[4] = className;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== children) {
      t2 = customRenderer(children);
      $[6] = children;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
      t3 = <li {...props} className={t1}>{t2}</li>;
      $[8] = props;
      $[9] = t1;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  };
  const BlockQuoteRenderer = t0 => {
    const $ = _c(12);
    let children;
    let className;
    let props;
    if ($[0] !== t0) {
      ({
        children,
        className,
        ...props
      } = t0);
      $[0] = t0;
      $[1] = children;
      $[2] = className;
      $[3] = props;
    } else {
      children = $[1];
      className = $[2];
      props = $[3];
    }
    let t1;
    if ($[4] !== className) {
      t1 = mergeClassNames("word-break tw-break-words tw-border-b-0 tw-border-l-4 tw-border-r-0 tw-border-t-0 tw-border-solid tw-border-l-iron-500 tw-pl-4 tw-text-iron-200", className);
      $[4] = className;
      $[5] = t1;
    } else {
      t1 = $[5];
    }
    let t2;
    if ($[6] !== children) {
      t2 = customRenderer(children);
      $[6] = children;
      $[7] = t2;
    } else {
      t2 = $[7];
    }
    let t3;
    if ($[8] !== props || $[9] !== t1 || $[10] !== t2) {
      t3 = <blockquote {...props} className={t1}>{t2}</blockquote>;
      $[8] = props;
      $[9] = t1;
      $[10] = t2;
      $[11] = t3;
    } else {
      t3 = $[11];
    }
    return t3;
  };
  return {
    h1: createHeadingRenderer("h1"),
    h2: createHeadingRenderer("h2"),
    h3: createHeadingRenderer("h3"),
    h4: createHeadingRenderer("h4"),
    h5: createHeadingRenderer("h5"),
    ...(renderParagraph ? {
      p: renderParagraph
    } : {}),
    li: ListItemRenderer,
    code: CodeRenderer,
    ...(renderAnchor ? {
      a: renderAnchor
    } : {}),
    ...(renderImage ? {
      img: renderImage
    } : {}),
    br: BreakComponent,
    blockquote: BlockQuoteRenderer
  } satisfies Components;
};
export interface DropPartMarkdownProps {
  readonly mentionedUsers: Array<ApiDropMentionedUser>;
  readonly mentionedGroups?: Array<ApiDropGroupMention> | undefined;
  readonly mentionedWaves: Array<ApiMentionedWave>;
  readonly referencedNfts: Array<ApiDropReferencedNFT>;
  readonly nftLinks?: readonly ApiDropNftLink[] | undefined;
  readonly partContent: string | null;
  readonly onQuoteClick: (drop: ApiDrop) => void;
  readonly textSize?: "sm" | "md" | undefined;
  readonly currentDropId?: string | undefined;
  readonly hideLinkPreviews?: boolean | undefined;
  readonly embedPath?: readonly string[] | undefined;
  readonly quotePath?: readonly string[] | undefined;
  readonly embedDepth?: number | undefined;
  readonly maxEmbedDepth?: number | undefined;
  readonly linkPreviewToggleControl?: LinkPreviewToggleControl | undefined;
  readonly onLinkCardActionsActiveChange?: ((href: string, active: boolean) => void) | undefined;
}
function DropPartMarkdown(t0) {
  const $ = _c(51);
  const {
    mentionedUsers,
    mentionedGroups: t1,
    mentionedWaves,
    referencedNfts,
    nftLinks,
    partContent,
    onQuoteClick,
    textSize,
    currentDropId,
    hideLinkPreviews: t2,
    embedPath,
    quotePath,
    embedDepth: t3,
    maxEmbedDepth: t4,
    linkPreviewToggleControl,
    onLinkCardActionsActiveChange
  } = t0;
  let t5;
  if ($[0] !== t1) {
    t5 = t1 === undefined ? [] : t1;
    $[0] = t1;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const mentionedGroups = t5;
  const hideLinkPreviews = t2 === undefined ? false : t2;
  const embedDepth = t3 === undefined ? 0 : t3;
  const maxEmbedDepth = t4 === undefined ? DEFAULT_MAX_EMBED_DEPTH : t4;
  const queryClient = useQueryClient();
  const isMobile = useIsMobileScreen();
  const {
    emojiMap,
    findNativeEmoji
  } = useEmoji();
  const seizeSettings = useSeizeSettingsOptional();
  const tweetPreviewMode = useTweetPreviewMode();
  const {
    variant: linkPreviewVariant
  } = useLinkPreviewContext();
  let t6;
  let t7;
  if ($[2] !== nftLinks || $[3] !== queryClient) {
    t6 = () => {
      primeMarketplacePreviewCacheFromNftLinks({
        queryClient,
        nftLinks
      });
    };
    t7 = [queryClient, nftLinks];
    $[2] = nftLinks;
    $[3] = queryClient;
    $[4] = t6;
    $[5] = t7;
  } else {
    t6 = $[4];
    t7 = $[5];
  }
  useLayoutEffect(t6, t7);
  let t8;
  bb0: switch (textSize) {
    case "sm":
      {
        t8 = isMobile ? "tw-text-xs" : "tw-text-sm";
        break bb0;
      }
    case "md":
    default:
      {
        t8 = "tw-text-md";
      }
  }
  const textSizeClass = t8;
  let t9;
  if ($[6] !== currentDropId || $[7] !== embedPath) {
    bb1: {
      const path = normalizeStringArray(embedPath);
      if (!currentDropId || path.includes(currentDropId)) {
        t9 = path;
        break bb1;
      }
      t9 = [...path, currentDropId];
    }
    $[6] = currentDropId;
    $[7] = embedPath;
    $[8] = t9;
  } else {
    t9 = $[8];
  }
  const normalizedEmbedPath = t9;
  let t10;
  if ($[9] !== quotePath) {
    t10 = normalizeStringArray(quotePath);
    $[9] = quotePath;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  const normalizedQuotePath = t10;
  let t11;
  if ($[11] !== currentDropId || $[12] !== embedDepth || $[13] !== hideLinkPreviews || $[14] !== maxEmbedDepth || $[15] !== normalizedEmbedPath || $[16] !== normalizedQuotePath || $[17] !== onQuoteClick || $[18] !== seizeSettings?.isMemesWave || $[19] !== tweetPreviewMode) {
    t11 = () => createLinkRenderer({
      onQuoteClick,
      currentDropId,
      hideLinkPreviews,
      tweetPreviewMode,
      isMemesWaveById: seizeSettings?.isMemesWave,
      embedPath: normalizedEmbedPath,
      quotePath: normalizedQuotePath,
      embedDepth,
      maxEmbedDepth
    });
    $[11] = currentDropId;
    $[12] = embedDepth;
    $[13] = hideLinkPreviews;
    $[14] = maxEmbedDepth;
    $[15] = normalizedEmbedPath;
    $[16] = normalizedQuotePath;
    $[17] = onQuoteClick;
    $[18] = seizeSettings?.isMemesWave;
    $[19] = tweetPreviewMode;
    $[20] = t11;
  } else {
    t11 = $[20];
  }
  seizeSettings?.isMemesWave;
  let t12;
  if ($[21] !== t11) {
    t12 = t11();
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  const {
    renderAnchor,
    isSmartLink,
    renderImage
  } = t12;
  let t13;
  if ($[23] !== emojiMap || $[24] !== findNativeEmoji || $[25] !== isSmartLink || $[26] !== mentionedGroups || $[27] !== mentionedUsers || $[28] !== mentionedWaves || $[29] !== referencedNfts || $[30] !== textSizeClass) {
    t13 = createMarkdownContentRenderers({
      textSizeClass,
      mentionedUsers,
      mentionedGroups,
      mentionedWaves,
      referencedNfts,
      emojiMap,
      findNativeEmoji,
      isSmartLink
    });
    $[23] = emojiMap;
    $[24] = findNativeEmoji;
    $[25] = isSmartLink;
    $[26] = mentionedGroups;
    $[27] = mentionedUsers;
    $[28] = mentionedWaves;
    $[29] = referencedNfts;
    $[30] = textSizeClass;
    $[31] = t13;
  } else {
    t13 = $[31];
  }
  const {
    customRenderer,
    renderParagraph,
    processContent
  } = t13;
  let t14;
  if ($[32] !== partContent || $[33] !== processContent) {
    t14 = processContent(partContent);
    $[32] = partContent;
    $[33] = processContent;
    $[34] = t14;
  } else {
    t14 = $[34];
  }
  const processedContent = t14;
  let t15;
  if ($[35] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = [rehypeExternalLinks, {
      target: "_blank",
      rel: ["noopener", "noreferrer", "nofollow"],
      protocols: ["http", "https"]
    }];
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
    t16 = [t15, [rehypeSanitize, {
      allowedTags: ["p", "br", "strong", "em", "a", "code", "pre", "blockquote", "h1", "h2", "h3", "h4", "h5", "h6", "ul", "ol", "li", "img"],
      allowedAttributes: {
        a: ["href", "title"],
        img: ["src", "alt", "title"],
        code: ["className"],
        pre: ["className"]
      }
    }]];
    $[36] = t16;
  } else {
    t16 = $[36];
  }
  const rehypePlugins = t16;
  let t17;
  if ($[37] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = [remarkGfm];
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  const remarkPlugins = t17;
  let t18;
  if ($[38] !== customRenderer || $[39] !== renderAnchor || $[40] !== renderImage || $[41] !== renderParagraph) {
    t18 = createMarkdownComponents({
      customRenderer,
      renderParagraph,
      renderAnchor,
      renderImage
    });
    $[38] = customRenderer;
    $[39] = renderAnchor;
    $[40] = renderImage;
    $[41] = renderParagraph;
    $[42] = t18;
  } else {
    t18 = $[42];
  }
  const markdownComponents = t18;
  let t19;
  if ($[43] !== markdownComponents || $[44] !== processedContent) {
    t19 = <Markdown rehypePlugins={rehypePlugins} remarkPlugins={remarkPlugins} className="tw-w-full" components={markdownComponents}>{processedContent}</Markdown>;
    $[43] = markdownComponents;
    $[44] = processedContent;
    $[45] = t19;
  } else {
    t19 = $[45];
  }
  let t20;
  if ($[46] !== linkPreviewToggleControl || $[47] !== linkPreviewVariant || $[48] !== onLinkCardActionsActiveChange || $[49] !== t19) {
    t20 = <LinkPreviewProvider variant={linkPreviewVariant} previewToggle={linkPreviewToggleControl} onCardActionsActiveChange={onLinkCardActionsActiveChange}>{t19}</LinkPreviewProvider>;
    $[46] = linkPreviewToggleControl;
    $[47] = linkPreviewVariant;
    $[48] = onLinkCardActionsActiveChange;
    $[49] = t19;
    $[50] = t20;
  } else {
    t20 = $[50];
  }
  return t20;
}
export { DropContentPartType };
export default memo(DropPartMarkdown);
function _temp(child) {
  if (typeof child === "string" || typeof child === "number") {
    return child;
  }
  return "";
}
function _temp2(error) {
  console.error("[DropPartMarkdown] Failed to highlight code", error);
}